import React, { useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { Database, Download, AlertTriangle, ShieldCheck, FileJson, BellRing, Mail, Eraser } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSystem() {
  const [downloading, setDownloading] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [runningReminders, setRunningReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const handleCleanupOrphans = async () => {
    if (!window.confirm('¿Eliminar registros de progreso huérfanos (sin asignación activa y no completados)? Esta acción no se puede deshacer.')) return;
    setCleaning(true);
    try {
      const r = await api.cleanupOrphanProgress();
      setCleanupResult(r.data);
      toast.success(`Depuración completa · ${r.data.deleted} registro(s) eliminado(s)`);
    } catch (e) {
      console.error(e);
      toast.error('Error al depurar progreso huérfano');
    } finally {
      setCleaning(false);
    }
  };

  const handleRunReminders = async () => {
    setRunningReminders(true);
    try {
      const r = await api.runReminders();
      setReminderResult(r.data);
      toast.success(`Recordatorios enviados: ${r.data.sent}`);
    } catch (e) {
      console.error(e);
      toast.error('Error al disparar recordatorios');
    } finally {
      setRunningReminders(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/system/backup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      // Try to extract filename from Content-Disposition
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : `proesa-backup-${Date.now()}.json`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const sizeKB = (blob.size / 1024).toFixed(1);
      setLastBackup({ filename: fname, size: sizeKB, at: new Date().toLocaleString('es-MX') });
      toast.success(`Respaldo descargado · ${sizeKB} KB`);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo generar el respaldo');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl" data-testid="admin-system-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
            <Database className="h-7 w-7 text-[#DC143C]" />
            Sistema · Respaldo
          </h1>
          <p className="text-gray-600 mt-1">Exporta toda la base de datos en formato JSON</p>
        </div>

        {/* Card principal: descargar respaldo */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="bg-red-50 p-3 rounded-lg flex-shrink-0">
              <FileJson className="h-8 w-8 text-[#DC143C]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Respaldo completo de la base de datos</h2>
              <p className="text-sm text-gray-600">
                Genera un archivo <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">.json</code> con todas las colecciones del sistema:
                departamentos, proyectos, empleados, usuarios, visitas, cursos, asignaciones, progreso, diplomas, intentos de evaluación e intranet.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-5">
            {[
              'departments', 'projects', 'employees', 'users', 'visits',
              'courses', 'course_assignments', 'course_progress',
              'diploma_templates', 'evaluation_attempts', 'intranet_posts',
            ].map((c) => (
              <span key={c} className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono truncate" data-testid="backup-collection-chip">
                {c}
              </span>
            ))}
          </div>

          <Button
            onClick={handleDownloadBackup}
            disabled={downloading}
            data-testid="download-backup-button"
            className="w-full md:w-auto bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center justify-center gap-2 px-6"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'Generando respaldo...' : 'Descargar respaldo JSON'}
          </Button>

          {lastBackup && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-start gap-2" data-testid="last-backup-info">
              <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Último respaldo descargado</p>
                <p className="text-xs text-green-700 mt-0.5">
                  <code>{lastBackup.filename}</code> · {lastBackup.size} KB · {lastBackup.at}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Card: recordatorios automáticos */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="bg-amber-50 p-3 rounded-lg flex-shrink-0">
              <BellRing className="h-8 w-8 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Recordatorios de expiración</h2>
              <p className="text-sm text-gray-600">
                Envía un email a los usuarios cuyo curso asignado <strong>expira en menos de 24 horas</strong> y aún no lo han completado.
                Se ejecuta automáticamente cada hora; este botón sirve para dispararlo manualmente (útil para probar).
                Cada usuario recibe el recordatorio sólo una vez por asignación.
              </p>
            </div>
          </div>

          <Button
            onClick={handleRunReminders}
            disabled={runningReminders}
            data-testid="run-reminders-button"
            className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center gap-2 px-6"
          >
            <Mail className="h-4 w-4" />
            {runningReminders ? 'Procesando...' : 'Ejecutar recordatorios ahora'}
          </Button>

          {reminderResult && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900" data-testid="reminder-result">
              <p className="font-medium mb-1 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" /> Resultado del último envío
              </p>
              <ul className="text-xs text-blue-800 space-y-0.5 ml-5 list-disc">
                <li><strong>{reminderResult.sent}</strong> correo(s) enviado(s)</li>
                <li><strong>{reminderResult.skipped_completed || 0}</strong> usuario(s) ya completaron el curso</li>
                <li><strong>{reminderResult.skipped_no_email || 0}</strong> usuario(s) sin email configurado</li>
                <li><strong>{reminderResult.skipped_expired || 0}</strong> asignación(es) ya expirada(s)</li>
                {reminderResult.skipped_no_smtp && (
                  <li className="text-amber-700 font-medium">SMTP no configurado — no se enviaron correos</li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Card: depurar progreso huérfano */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="bg-purple-50 p-3 rounded-lg flex-shrink-0">
              <Eraser className="h-8 w-8 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Depurar progreso huérfano</h2>
              <p className="text-sm text-gray-600">
                Elimina registros de <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">course_progress</code> con estado
                <span className="font-medium"> &quot;En curso&quot;</span> cuya asignación ya no existe (curso o asignación eliminada).
                Estos registros aparecen incorrectamente en el <strong>Reporte de Capacitación</strong>. Los progresos <em>completados</em>
                nunca se eliminan (se preservan para historial y diplomas).
              </p>
            </div>
          </div>

          <Button
            onClick={handleCleanupOrphans}
            disabled={cleaning}
            data-testid="cleanup-orphan-button"
            className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2 px-6"
          >
            <Eraser className="h-4 w-4" />
            {cleaning ? 'Depurando...' : 'Depurar progreso huérfano'}
          </Button>

          {cleanupResult && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded text-sm text-purple-900" data-testid="cleanup-result">
              <p className="font-medium mb-1 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" /> Resultado
              </p>
              <ul className="text-xs text-purple-800 space-y-0.5 ml-5 list-disc">
                <li><strong>{cleanupResult.scanned}</strong> registro(s) revisado(s)</li>
                <li><strong>{cleanupResult.deleted}</strong> registro(s) huérfano(s) eliminado(s)</li>
              </ul>
            </div>
          )}
        </div>

        {/* Aviso de seguridad */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-900 mb-1">Información sensible</p>
            <ul className="text-amber-800 space-y-1 list-disc list-inside">
              <li>El archivo contiene <strong>contraseñas hasheadas</strong> de todos los usuarios (necesarias para restaurar el sistema). Guárdalo en un lugar seguro.</li>
              <li>Para usarlo en otro proveedor de hosting (Hostinger, AWS, etc.), tu app necesita MongoDB. Hostinger solo ofrece MySQL — necesitarás usar <strong>MongoDB Atlas (capa gratuita)</strong> como base de datos remota.</li>
              <li>Para restaurar: usa <code className="text-xs bg-amber-100 px-1 py-0.5 rounded">mongoimport</code> por colección, o un script que lea el JSON y haga <code className="text-xs bg-amber-100 px-1 py-0.5 rounded">insert_many</code>.</li>
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
